import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, MessageSquare, CheckCircle, Lock } from "lucide-react";
import "./ContactFormPopup.css";
import { usePopup } from "../../context/PopupContext";
import { gtagSendEvent } from "../../utils/gtaghelper";

const API_URL = "https://ca-email-sender.quantech.pro/api/form";

const ContactFormPopup = () => {
  const { isOpen, meta, closePopup, openPopup } = usePopup();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    details: "",
  });

  // Auto-open popup after 7 seconds (only if not already opened by a CTA)
  useEffect(() => {
    const timer = setTimeout(() => {
      openPopup({ source: "Auto — 7 s timer" });
    }, 7000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* =============================
     🔥 REAL SUBMIT
  ============================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: form.name,
          email:    form.email,
          number:   form.number,
          message:  form.details || "NA",
          subject:  "Free Consultation Request",
          // ── hidden context fields sent by every CTA ──
          source:   meta.source || "Unknown",
          plan:     meta.plan   || "",
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Submission failed");
gtagSendEvent("contact_form_Pop_submission")
      setSubmitted(true);

      setTimeout(() => {
        closePopup();
        setSubmitted(false);
        setForm({ name: "", email: "", number: "", details: "" });
      }, 3000);
    } catch (err) {
      console.error("Submit error:", err);
      alert("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <motion.button
        className="cfp-trigger"
        onClick={() => openPopup({ source: "Floating Chat Button" })}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open contact form"
      >
        <MessageSquare strokeWidth={2} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="cfp-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closePopup();
            }}
          >
            <motion.div
              className="cfp-card"
              initial={{ scale: 0.85, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
            >
              <button className="cfp-close" onClick={closePopup}>
                <X size={16} />
              </button>

              {!submitted ? (
                <>
                  <div className="cfp-header">
                    <div className="cfp-icon-ring">
                      <Send size={22} />
                    </div>
                    <h3 className="cfp-title">
                      Let's Build Something <span>Amazing</span>
                    </h3>
                    <p className="cfp-desc">
                      Tell us about your project and we'll get back within 24 hours.
                    </p>
                    {/* Show the plan / source context to the user if a plan was selected */}
                    {meta.plan && (
                      <p className="cfp-meta-badge">
                        Selected plan: <strong>{meta.plan}</strong>
                      </p>
                    )}
                  </div>

                  <form className="cfp-form" onSubmit={handleSubmit}>
                    {/* ── HIDDEN FIELDS (not rendered, sent via JSON body) ── */}
                    <input type="hidden" name="source" value={meta.source || ""} />
                    <input type="hidden" name="plan" value={meta.plan || ""} />

                    <div className="cfp-row">
                      <div className="cfp-field">
                        <label className="cfp-label">Name</label>
                        <input
                          className="cfp-input"
                          type="text"
                          name="name"
                          placeholder="John Doe"
                          value={form.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="cfp-field">
                        <label className="cfp-label">Email</label>
                        <input
                          className="cfp-input"
                          type="email"
                          name="email"
                          placeholder="john@example.com"
                          value={form.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="cfp-field">
                      <label className="cfp-label">Mobile Number</label>
                      <input
                        className="cfp-input"
                        type="tel"
                        name="number"
                        placeholder="Phone number"
                        value={form.number}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <button type="submit" className="cfp-submit" disabled={loading}>
                      <Send size={18} />
                      {loading ? "Sending..." : "Send Message"}
                      <span className="cfp-shine" />
                    </button>
                  </form>

                  <p className="cfp-privacy">
                    <Lock size={12} />
                    Your info is safe. We never spam or share your data.
                  </p>
                </>
              ) : (
                <motion.div
                  className="cfp-success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="cfp-success-icon">
                    <CheckCircle />
                  </div>
                  <h3>Message Sent! 🎉</h3>
                  <p>
                    Thanks for reaching out! We'll get back to you within 24 hours.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactFormPopup;